import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, Route, CalendarDays, Coins, HandCoins, CircleAlert, AlertTriangle, FileWarning, Wallet, MapPinned, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { kwd, fmtDateTime, TRIP_STATUS, todayISO } from "@/lib/util";
import { Loader, Pill, Money, tripColor, Section } from "@/components/common";
import { PageTitle, Stat } from "@/pages/admin/ui";
import ArchiveButton from "@/components/ArchiveButton";
import StaffActivity from "@/components/StaffActivity";
import FeesDue from "@/components/FeesDue";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loader />;
  if (!d) return <p>تعذر تحميل البيانات</p>;
  const K = (f) => `${kwd(f)} د.ك`;
  const today = todayISO();
  const month = today.slice(0, 8) + "01";
  return (
    <div>
      <PageTitle title="لوحة التحكم" sub="نظرة عامة على أعمال الشركة">{user.role === "admin" && <ArchiveButton />}</PageTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat to="/admin/drivers" testid="stat-drivers-total" label="إجمالي السائقين" value={d.drivers_total} icon={Users} />
        <Stat to="/admin/drivers?status=active" testid="stat-drivers-active" label="السائقون النشطون" value={d.drivers_active} icon={UserCheck} tone="green" />
        <Stat to={`/admin/trips?date_from=${today}&date_to=${today}`} testid="stat-trips-today" label="نقلات اليوم" value={d.trips_today} icon={Route} tone="blue" />
        <Stat to={`/admin/trips?date_from=${month}&date_to=${today}`} testid="stat-trips-month" label="نقلات هذا الشهر" value={d.trips_month} icon={CalendarDays} tone="blue" />
        <Stat to="/admin/trips" testid="stat-trips-value" label="إجمالي قيمة النقلات" value={K(d.trips_value_fils)} icon={Coins} tone="blue" />
        <Stat to="/admin/invoices?status=paid" testid="stat-collected" label="المبالغ المحصلة" value={K(d.collected_fils)} icon={HandCoins} tone="green" />
        <Stat to="/admin/invoices?status=unpaid" testid="stat-unpaid" label="المبالغ غير المدفوعة" value={K(d.unpaid_fils)} icon={CircleAlert} tone="red" />
        <Stat to="/admin/violations" testid="stat-violations" label={`المخالفات (${d.violations_unseen} لم يُطلع عليها)`} value={d.violations_total} icon={AlertTriangle} tone="red" />
        <Stat to="/admin/documents?status=alert" testid="stat-docs-expiring" label="مستندات قريبة الانتهاء" value={d.docs_expiring} icon={FileWarning} tone="green" />
        <Stat to="/admin/fees" testid="stat-fees-total" label="الرسوم الإدارية المطلوبة" value={K(d.fees_total_fils)} icon={Wallet} tone="orange" />
        <Stat to="/admin/fees" testid="stat-fees-paid" label="دفعات الرسوم" value={K(d.fees_paid_fils)} icon={Wallet} tone="orange" />
        <Stat to="/admin/invoices" testid="stat-link-pending" label="دفعات روابط بانتظار التأكيد" value={d.pending_link_payments} icon={Link2} tone="orange" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Section title="آخر النقلات" action={<Link to="/admin/trips" className="text-blue-700 font-bold">الكل</Link>}>
          <div className="divide-y">
            {d.recent_trips.length === 0 && <p className="text-slate-500 py-4">لا توجد نقلات بعد</p>}
            {d.recent_trips.map((t) => (
              <Link key={t.id} to={`/admin/trips/${t.id}`} className="flex items-center justify-between py-3 gap-3 hover:bg-slate-50 px-1 rounded">
                <div className="min-w-0"><span className="ltr font-bold">{t.number}</span> <span className="text-slate-500 text-sm">{t.driver_name}</span>
                  <div className="text-sm text-slate-500 truncate"><bdi>{t.pickup}</bdi> ← <bdi>{t.dropoff}</bdi></div></div>
                <div className="text-left shrink-0"><Money fils={t.price_fils} /><div><Pill color={tripColor(t.status)}>{TRIP_STATUS[t.status]}</Pill></div></div>
              </Link>
            ))}
          </div>
        </Section>
        <Section title={<span className="flex items-center gap-2"><MapPinned className="w-5 h-5 text-blue-700" />آخر تغييرات الوجهة</span>}>
          <div className="divide-y" data-testid="recent-destination-changes">
            {d.recent_destination_changes.length === 0 && <p className="text-slate-500 py-4">لا توجد تغييرات</p>}
            {d.recent_destination_changes.map((c) => (
              <Link key={c.id} to={`/admin/trips/${c.trip_id}`} className="block py-3 hover:bg-slate-50 px-1 rounded">
                <div><span className="ltr font-bold">{c.trip_number}</span> • {c.by_name}</div>
                <div className="text-sm"><s className="text-slate-400">{c.old}</s> ← <b>{c.new}</b></div>
                <div className="text-xs text-slate-500">{fmtDateTime(c.at)}</div>
              </Link>
            ))}
          </div>
        </Section>
        {user.role === "admin" && <FeesDue />}
        {user.role === "admin" && <StaffActivity />}
      </div>
    </div>
  );
}
